"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/formatters/MXPIcon";

// Jun 26 - converted to use React-dom Portal functionality: https://react.dev/reference/react-dom/createPortal#usage

/* Gap (px) between the trigger and the tooltip bubble, per direction. */
const GAP = {
  up: 8,
  down: 8,
  bottom: 8,
  left: 12,
  right: 12
};

/*
 * Transform used to anchor the bubble against the computed coordinate.
 * The coordinate is the edge/center of the trigger; the transform shifts
 * the bubble so it sits on the correct side and stays centered.
 */
const transformByDirection = {
  up: "translate(-50%, -100%)",
  down: "translate(-50%, 0)",
  bottom: "translate(-50%, 0)",
  left: "translate(-100%, -50%)",
  right: "translate(0, -50%)"
};

/* Arrow (the before:* pseudo element) classes, per direction. */
const arrowByDirection = {
  up: "before:top-full before:left-1/2 before:-translate-x-1/2 before:border-t-gray-m9",
  down: "before:bottom-full before:left-1/2 before:-translate-x-1/2 before:border-b-gray-m9",
  bottom: "before:bottom-full before:left-1/2 before:-translate-x-1/2 before:border-b-gray-m9",
  left: "before:left-full before:top-1/2 before:-translate-y-1/2 before:border-l-gray-m9",
  right: "before:right-full before:top-1/2 before:-translate-y-1/2 before:border-r-gray-m9"
};
const Tooltip = t0 => {
  const $ = _c(36);
  const {
    tooltipLabel,
    tooltipIcon,
    direction: t1,
    children,
    iconSize: t2
  } = t0;
  const direction = t1 === undefined ? "up" : t1;
  const iconSize = t2 === undefined ? 15 : t2;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      top: 0,
      left: 0
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [coords, setCoords] = useState(t3);
  const triggerRef = useRef(null);
  const dir = transformByDirection[direction] ? direction : "up";
  const gap = GAP[dir];
  const arrow = arrowByDirection[dir];
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setMounted(true);
    };
    t5 = [];
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  let t6;
  if ($[3] !== dir || $[4] !== gap) {
    t6 = () => {
      const el = triggerRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      let top;
      let left;
      bb18: switch (dir) {
        case "left":
          {
            left = rect.left - gap;
            top = rect.top + rect.height / 2;
            break bb18;
          }
        case "right":
          {
            left = rect.right + gap;
            top = rect.top + rect.height / 2;
            break bb18;
          }
        case "down":
        case "bottom":
          {
            left = rect.left + rect.width / 2;
            top = rect.bottom + gap;
            break bb18;
          }
        case "up":
        default:
          {
            left = rect.left + rect.width / 2;
            top = rect.top - gap;
          }
      }
      setCoords({
        top,
        left
      });
    };
    $[3] = dir;
    $[4] = gap;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const updatePosition = t6;
  let t7;
  if ($[6] !== updatePosition) {
    t7 = () => {
      updatePosition();
      setTooltipVisible(true);
    };
    $[6] = updatePosition;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const showTooltip = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setTooltipVisible(false);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const hideTooltip = t8;
  let t9;
  if ($[9] !== showTooltip || $[10] !== tooltipVisible) {
    t9 = () => {
      if (tooltipVisible) {
        hideTooltip();
      } else {
        showTooltip();
      }
    };
    $[9] = showTooltip;
    $[10] = tooltipVisible;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const toggleTooltip = t9;
  let t10;
  let t11;
  if ($[12] !== tooltipVisible || $[13] !== updatePosition) {
    t10 = () => {
      if (!tooltipVisible) {
        return;
      }
      const handleReposition = () => updatePosition();
      window.addEventListener("scroll", handleReposition, true);
      window.addEventListener("resize", handleReposition);
      return () => {
        window.removeEventListener("scroll", handleReposition, true);
        window.removeEventListener("resize", handleReposition);
      };
    };
    t11 = [tooltipVisible, updatePosition];
    $[12] = tooltipVisible;
    $[13] = updatePosition;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  if ($[16] !== tooltipIcon || $[17] !== tooltipLabel) {
    t12 = tooltipLabel && !tooltipIcon && <span>{tooltipLabel}</span>;
    $[16] = tooltipIcon;
    $[17] = tooltipLabel;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== iconSize || $[20] !== tooltipIcon || $[21] !== tooltipLabel) {
    t13 = tooltipLabel && tooltipIcon && <Icon type={tooltipIcon ? tooltipIcon : null} size={iconSize} />;
    $[19] = iconSize;
    $[20] = tooltipIcon;
    $[21] = tooltipLabel;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== arrow || $[24] !== children || $[25] !== coords || $[26] !== dir || $[27] !== mounted || $[28] !== tooltipVisible) {
    t14 = mounted && tooltipVisible && createPortal(<span role="tooltip" style={{
      position: "fixed",
      top: `${coords.top}px`,
      left: `${coords.left}px`,
      transform: transformByDirection[dir]
    }} className={`pointer-events-none z-[9999] block w-max max-w-[300px] rounded-lg bg-gray-m9 px-3 py-2 text-sm font-normal leading-5 text-white shadow-md before:absolute before:border-[5px] before:border-transparent before:content-[''] ${arrow}`}>{children}</span>, document.body);
    $[23] = arrow;
    $[24] = children;
    $[25] = coords;
    $[26] = dir;
    $[27] = mounted;
    $[28] = tooltipVisible;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== showTooltip || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== toggleTooltip) {
    t15 = <span ref={triggerRef} className="group relative inline-block cursor-pointer" onClick={toggleTooltip} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>{t12}{t13}{t14}</span>;
    $[30] = showTooltip;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = toggleTooltip;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
export default Tooltip;